import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Dropdown,
  Form,
  Modal,
  Row,
  Spinner,
} from 'react-bootstrap';
import { useLanguage } from '../../../context/LanguageContext';
import { MARKETPLACE_ICONS } from '../../../assets/marketplaceIcons';
import { AddStoreModal } from '../../Store';
import api from '../../../services/api';

const getStoreIcon = (storeType) => MARKETPLACE_ICONS[storeType] || MARKETPLACE_ICONS.wildberries;

export default function ChatAppStoresSection() {
  const { t } = useLanguage();
  const [stores, setStores] = useState([]);
  const [showAddStore, setShowAddStore] = useState(false);
  const [showEditStore, setShowEditStore] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [editStoreName, setEditStoreName] = useState('');
  const [editStoreLoading, setEditStoreLoading] = useState(false);
  const [editStoreError, setEditStoreError] = useState('');
  const [showDeleteStore, setShowDeleteStore] = useState(false);
  const [deletingStore, setDeletingStore] = useState(null);
  const [deleteStoreLoading, setDeleteStoreLoading] = useState(false);
  const [showUpdateApiKey, setShowUpdateApiKey] = useState(false);
  const [updatingStore, setUpdatingStore] = useState(null);
  const [newApiKey, setNewApiKey] = useState('');
  const [updateApiKeyLoading, setUpdateApiKeyLoading] = useState(false);
  const [updateApiKeyError, setUpdateApiKeyError] = useState('');
  const [updateApiKeySuccess, setUpdateApiKeySuccess] = useState('');

  const loadStores = useCallback(async () => {
    try {
      const response = await api.getStores();
      setStores(response || []);
    } catch (err) {
      console.error('Failed to load stores:', err);
    }
  }, []);

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  useEffect(() => {
    const pendingStores = stores.filter((s) => s.verification_status === 'pending');
    if (pendingStores.length === 0) return;
    const tick = async () => {
      for (const store of pendingStores) {
        try {
          const response = await api.getStoreVerificationStatus(store.id);
          if (response.verification_status !== 'pending') {
            loadStores();
            break;
          }
        } catch (err) {
          console.error(err);
        }
      }
    };
    const id = setInterval(tick, 15000);
    return () => clearInterval(id);
  }, [stores, loadStores]);

  const handleEditStore = (store, e) => {
    e.stopPropagation();
    setEditingStore(store);
    setEditStoreName(store.name || '');
    setEditStoreError('');
    setShowEditStore(true);
  };

  const handleDeleteStore = (store, e) => {
    e.stopPropagation();
    setDeletingStore(store);
    setShowDeleteStore(true);
  };

  const confirmDeleteStore = async () => {
    if (!deletingStore) return;
    setDeleteStoreLoading(true);
    try {
      await api.deleteStore(deletingStore.id);
      setStores((prev) => prev.filter((s) => s.id !== deletingStore.id));
      setShowDeleteStore(false);
      setDeletingStore(null);
    } finally {
      setDeleteStoreLoading(false);
    }
  };

  const handleSaveStoreName = async () => {
    if (!editingStore) return;
    setEditStoreLoading(true);
    setEditStoreError('');
    try {
      await api.updateStore(editingStore.id, {
        name: editStoreName.trim() || `Store ${editingStore.store_id}`,
      });
      setStores((prev) =>
        prev.map((s) =>
          s.id === editingStore.id
            ? { ...s, name: editStoreName.trim() || `Store ${editingStore.store_id}` }
            : s,
        ),
      );
      setShowEditStore(false);
    } catch (err) {
      setEditStoreError(err.data?.detail || t('stores.editError'));
    } finally {
      setEditStoreLoading(false);
    }
  };

  const handleUpdateApiKey = (store, e) => {
    e.stopPropagation();
    setUpdatingStore(store);
    setNewApiKey('');
    setUpdateApiKeyError('');
    setUpdateApiKeySuccess('');
    setShowUpdateApiKey(true);
  };

  const handleSaveApiKey = async () => {
    if (!updatingStore || !newApiKey.trim()) return;
    setUpdateApiKeyLoading(true);
    setUpdateApiKeyError('');
    setUpdateApiKeySuccess('');
    try {
      await api.updateStore(updatingStore.id, { api_key: newApiKey.trim() });
      setUpdateApiKeySuccess(t('stores.apiKeyUpdateSuccess'));
      setStores((prev) =>
        prev.map((s) =>
          s.id === updatingStore.id ? { ...s, verification_status: 'pending' } : s,
        ),
      );
      setTimeout(() => setShowUpdateApiKey(false), 1200);
    } catch (err) {
      setUpdateApiKeyError(err.data?.detail || t('stores.apiKeyUpdateError'));
    } finally {
      setUpdateApiKeyLoading(false);
    }
  };

  return (
    <Card className="border-0 shadow-sm mt-3">
      <Card.Header className="bg-transparent d-flex justify-content-between align-items-center">
        <h6 className="mb-0 fw-bold">{t('stores.title')}</h6>
        <Button size="sm" variant="outline-primary" onClick={() => setShowAddStore(true)}>
          <i className="bi bi-plus-lg me-1" />
          {t('nav.addStore')}
        </Button>
      </Card.Header>
      <Card.Body>
        {stores.some((s) => s.verification_status === 'failed') && (
          <Alert variant="danger" className="mb-3 py-2 small">
            {t('stores.verificationFailedMessage')}
          </Alert>
        )}
        {stores.length === 0 ? (
          <p className="text-muted small mb-0">{t('stores.noStores')}</p>
        ) : (
          <Row className="g-2">
            {stores.map((store) => (
              <Col key={store.id} xs={12} sm={6}>
                <Card className={`h-100 ${store.verification_status === 'failed' ? 'border-danger' : ''}`}>
                  <Card.Body className="py-2 px-3">
                    <div className="d-flex align-items-center gap-2">
                      <img
                        src={getStoreIcon(store.store_type)}
                        alt=""
                        style={{ width: 32, height: 32, borderRadius: 6 }}
                      />
                      <div className="flex-grow-1 min-w-0">
                        <div className="fw-semibold text-truncate">{store.name || `Store ${store.store_id}`}</div>
                        <small className="text-muted">{store.store_id}</small>
                      </div>
                      <Dropdown align="end">
                        <Dropdown.Toggle variant="link" size="sm" className="p-0 text-secondary" />
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={(e) => handleEditStore(store, e)}>
                            {t('stores.edit')}
                          </Dropdown.Item>
                          <Dropdown.Item onClick={(e) => handleUpdateApiKey(store, e)}>
                            {t('stores.updateApiKey')}
                          </Dropdown.Item>
                          <Dropdown.Item className="text-danger" onClick={(e) => handleDeleteStore(store, e)}>
                            {t('stores.deleteStore')}
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                    {store.verification_status === 'verified' && (
                      <Badge bg="success" className="mt-1">{t('stores.connected')}</Badge>
                    )}
                    {store.verification_status === 'pending' && (
                      <Badge bg="warning" text="dark" className="mt-1">{t('stores.verifying')}</Badge>
                    )}
                    {store.verification_status === 'failed' && (
                      <Badge bg="danger" className="mt-1">{t('stores.verificationFailed')}</Badge>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Card.Body>

      <AddStoreModal
        show={showAddStore}
        onHide={() => setShowAddStore(false)}
        onStoreAdded={(s) => {
          setStores((prev) => [...prev, s]);
          setShowAddStore(false);
        }}
      />

      <Modal show={showEditStore} onHide={() => setShowEditStore(false)} centered>
        <Modal.Header closeButton><Modal.Title>{t('stores.edit')}</Modal.Title></Modal.Header>
        <Modal.Body>
          {editStoreError && <Alert variant="danger">{editStoreError}</Alert>}
          <Form.Control
            value={editStoreName}
            onChange={(e) => setEditStoreName(e.target.value)}
            placeholder={t('stores.storeName')}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditStore(false)}>{t('common.cancel')}</Button>
          <Button className="btn-gradient" onClick={handleSaveStoreName} disabled={editStoreLoading}>
            {editStoreLoading ? <Spinner size="sm" /> : t('common.save')}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeleteStore} onHide={() => setShowDeleteStore(false)} centered>
        <Modal.Header closeButton><Modal.Title>{t('stores.deleteStore')}</Modal.Title></Modal.Header>
        <Modal.Body>{t('stores.deleteStoreConfirm')}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteStore(false)}>{t('common.cancel')}</Button>
          <Button variant="danger" onClick={confirmDeleteStore} disabled={deleteStoreLoading}>
            {deleteStoreLoading ? <Spinner size="sm" /> : t('stores.deleteStore')}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showUpdateApiKey} onHide={() => setShowUpdateApiKey(false)} centered>
        <Modal.Header closeButton><Modal.Title>{t('stores.updateApiKey')}</Modal.Title></Modal.Header>
        <Modal.Body>
          {updateApiKeyError && <Alert variant="danger">{updateApiKeyError}</Alert>}
          {updateApiKeySuccess && <Alert variant="success">{updateApiKeySuccess}</Alert>}
          <Form.Control
            type="password"
            value={newApiKey}
            onChange={(e) => setNewApiKey(e.target.value)}
            placeholder={t('stores.apiKeyPlaceholder')}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUpdateApiKey(false)}>{t('common.cancel')}</Button>
          <Button className="btn-gradient" onClick={handleSaveApiKey} disabled={updateApiKeyLoading}>
            {updateApiKeyLoading ? <Spinner size="sm" /> : t('common.save')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
}
